import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import toast from 'react-hot-toast';

import type { AssetResponse, BulkResultResponse } from '../../api';
import { getAsset, updateAssets } from '../../api';
import { assetRulesOf, specOf } from '../../constants/assetTypes';
import type { MetadataFieldKey } from '../../constants/metadataFields';
import { UNKNOWN_VALUES } from '../../constants/taxonomy';
import { MetadataFields } from '../metadata/MetadataFields';
import {
  buildUpdateFields,
  deriveFormState,
  EDITABLE_FIELDS,
  getUnsavableClears,
  hasChanges,
  mergeFormState,
  type EditableField,
  type FormState,
} from './bulkEditState';
import { useFocusWhenRendered } from '../metadata/useFocusWhenRendered';
import { SourceImageSection } from './SourceImageSection';

/**
 * The metadata editor itself: fields, dirty tracking, save.
 *
 * One component serves single and bulk editing. That is why the editor is a
 * form over a selection rather than inline fields on a card: an inline editor
 * only ever knows about one asset, so bulk edit would need a second form with
 * its own validation, dirty tracking and mutation call site. Here, editing one
 * asset is simply the case where the selection has one member.
 *
 * The fields themselves live in `MetadataFields`, shared with the upload
 * modal's Details step so the two can never drift. This component adds what
 * only the drawer needs: mixed-state derivation, dirty tracking, the
 * read-only identity block and the save mutation.
 *
 * Deliberately unstyled at the edges — no border, no padding, no width. The
 * container decides those.
 */

/**
 * What an outside caller can do with the form's unsaved edits.
 *
 * Exists for the overlay's Publish button, which sits in the header — outside
 * this component — and must not publish stale rows while the rail still holds
 * edits the user typed and hasn't saved. Deliberately narrow: commit the edits,
 * nothing else. Lifting the whole form into the overlay would drag mixed-state
 * derivation and dirty tracking up with it, and the form is also used where
 * there is no overlay at all.
 */
export interface MetadataFormHandle {
  /** Run the save. Resolves true only if every asset updated cleanly. */
  save: () => Promise<boolean>;
  /**
   * Take the user to the copy field: a headline row to type in if there is no
   * copy yet, and the cursor in it. What a caption's "Add copy" / "Edit" calls.
   */
  focusCopy: () => void;
}

interface AssetMetadataFormProps {
  selectedAssets: AssetResponse[];
  /** When true the form is read-only and cannot be saved (e.g. published/expired assets). */
  readOnly?: boolean;
  /**
   * Filled with the save handle. A ref, not a prop, because the caller only
   * ever invokes it from a click — routing an imperative action through render
   * state would re-render the caller on every keystroke in the rail.
   */
  handleRef?: MutableRefObject<MetadataFormHandle | null>;
  /**
   * Fired when the form crosses between "nothing to save" and "something to
   * save". This one *does* have to reach render — the overlay's button changes
   * its label and its disabled rule on it — but only on the transition, not on
   * every character typed.
   */
  onDirtyChange?: (isDirty: boolean) => void;
}

export function AssetMetadataForm({
  selectedAssets,
  readOnly = false,
  handleRef,
  onDirtyChange,
}: AssetMetadataFormProps) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(() => deriveFormState(selectedAssets));
  const [dirty, setDirty] = useState<Set<EditableField>>(() => new Set());

  // Two keys, because the form has to react to two different events.
  //
  // `selectionKey` is what is selected. Changing it resets the form and
  // discards half-typed edits — those edits were scoped to the old selection
  // and applying them to a different one is never what was meant. In the
  // overlay this also covers stepping to the next asset with the arrow keys.
  //
  // `revisionKey` is when those same rows last changed. Linking a source image
  // copies the hero's metadata onto the very assets the drawer is showing, so
  // without this the fields would still read empty after the copy landed.
  const selectionKey = useMemo(
    () =>
      selectedAssets
        .map((asset) => asset.id)
        .sort()
        .join(','),
    [selectedAssets],
  );
  const revisionKey = useMemo(
    () =>
      selectedAssets
        .map((asset) => `${asset.id}:${asset.updated_at}`)
        .sort()
        .join(','),
    [selectedAssets],
  );

  useEffect(() => {
    setForm(deriveFormState(selectedAssets));
    setDirty(new Set());
    // `selectedAssets` is a fresh array each render; `selectionKey` is the
    // stable identity of what's actually selected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey]);

  useEffect(() => {
    // Per field, not all-or-nothing. A refetch must not yank a field out from
    // under someone mid-edit — their own Save is what resolves that, and dirty
    // fields are the only ones Save sends — but the fields they are *not*
    // editing have to follow the row. Skipping the whole refresh because one
    // field is dirty is how linking a source image while half-way through typing
    // leaves six other fields quietly displaying pre-link values.
    setForm((current) => mergeFormState(current, deriveFormState(selectedAssets), dirty));
    // `dirty` is read, not depended on: a field going dirty must not re-run
    // this, and it is re-read on the next revision anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revisionKey]);

  // `quiet` suppresses only the success toast, for the overlay's
  // "Save & publish": that path is about to raise its own toast, and two
  // stacked green toasts for one click read as two separate things having
  // happened. Failures are never quiet — a save that didn't land is exactly
  // what has to interrupt the publish.
  const mutation = useMutation<BulkResultResponse, Error, { quiet: boolean }>({
    mutationFn: () =>
      updateAssets({
        asset_ids: selectedAssets.map((asset) => asset.id),
        fields: buildUpdateFields(form, dirty),
      }),
    onSuccess: (result, variables) => {
      // Bulk operations are partial-success, so the skipped list has to be
      // surfaced. Reporting only the count that changed would let a user walk
      // away believing all 500 rows were updated when some were not.
      if (result.skipped.length > 0) {
        toast.error(
          `Updated ${result.updated.length}, skipped ${result.skipped.length}: ${result.skipped[0].reason}`,
        );
      } else if (!variables.quiet) {
        toast.success(
          `Updated ${result.updated.length} asset${result.updated.length === 1 ? '' : 's'}`,
        );
      }
      setDirty(new Set());
      void queryClient.invalidateQueries({ queryKey: ['asset-library', 'assets'] });
      // One asset's own query too: answering a source image here unlocks the
      // channel rights of a crop edited next (`unknownChannelSources`).
      void queryClient.invalidateQueries({ queryKey: ['asset-library', 'asset'] });
      void queryClient.invalidateQueries({ queryKey: ['asset-library', 'campaigns'] });
    },
    onError: () => toast.error('Could not save changes'),
  });

  const rootRef = useRef<HTMLDivElement>(null);
  const focusCopyInput = useFocusWhenRendered(rootRef, '[data-copy-field] input[type="text"]');

  const setField = <K extends MetadataFieldKey>(field: K, value: FormState['value'][K]) => {
    // MetadataFields only emits the editable subset; the cast documents that.
    const editable = field as EditableField;
    if (!(EDITABLE_FIELDS as readonly string[]).includes(editable)) return;
    setForm((current) => {
      const mixed = new Set(current.mixed);
      mixed.delete(editable);
      return { value: { ...current.value, [field]: value }, mixed };
    });
    setDirty((current) => new Set(current).add(editable));
  };

  // Named separately because these are three different questions. A field the
  // user cleared is a real edit that cannot be sent, so it must block Save and
  // Save & publish — but it is not "nothing changed", which is what
  // `hasChanges` alone would report.
  const unsavableClears = readOnly ? [] : getUnsavableClears(form, dirty);
  const isDirty = !readOnly && (hasChanges(form, dirty) || unsavableClears.length > 0);
  const canSave = isDirty && unsavableClears.length === 0 && !mutation.isPending;
  const blockedReason =
    unsavableClears.length > 0
      ? `${unsavableClears.join(', ')} cannot be empty — publishing requires ${
          unsavableClears.length === 1 ? 'it' : 'them'
        }`
      : undefined;

  // In an effect, not the render body. Assigning a ref during render is a write
  // from a render that React is free to discard, and the closure captured there
  // can be one belonging to a throw-away pass. After commit it is simply the
  // current one, and a click cannot happen before the commit that drew the
  // button.
  useEffect(() => {
    if (!handleRef) return;
    handleRef.current = {
      focusCopy: () => {
        if (readOnly) return;
        if (form.value.copy_elements.length === 0) {
          setField('copy_elements', [{ type: 'headline', value: '' }]);
        }
        focusCopyInput();
      },
      save: async () => {
        // A cleared required field would be dropped from the body, so the
        // request would "succeed" having changed nothing and publish would go
        // ahead on the stale value. Refuse here instead, and say which field.
        if (unsavableClears.length > 0) {
          toast.error(blockedReason ?? 'Fix the highlighted fields before saving');
          return false;
        }
        try {
          const result = await mutation.mutateAsync({ quiet: true });
          return result.skipped.length === 0;
        } catch {
          // Swallowed deliberately and reported as false: `onError` has already
          // raised the toast, and the caller's only question is whether it may
          // go on to publish.
          return false;
        }
      },
    };
  });

  useEffect(() => {
    onDirtyChange?.(isDirty);
    // Only the flag. `onDirtyChange` is not depended on so a caller passing an
    // inline arrow doesn't turn this into an every-render notification.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  // The kinds in the selection decide which fields the form shows: one kind
  // shows everything it owns, a mixed selection only what they all own.
  // Rules, not kinds: a carousel master shows neither copy nor format.
  const assetTypes = useMemo(() => assetRulesOf(selectedAssets), [selectedAssets]);
  const onlyCarousels =
    selectedAssets.length > 0 && selectedAssets.every((asset) => specOf(asset).hasFrames);

  // Read-only facts about the selection. One value when every asset agrees,
  // "Mixed" otherwise — the same rule the editable fields follow, applied to
  // things nobody edits: the size, the ratio it reduces to, and the
  // server-set market and language of a master.
  const facts = useMemo(() => {
    const shared = (read: (asset: AssetResponse) => string | null): string | null => {
      const values = selectedAssets.map(read);
      const first = values[0] ?? null;
      return values.every((value) => value === first) ? first : 'Mixed';
    };
    return {
      dimensions: shared((a) =>
        a.width !== null && a.height !== null ? `${a.width}×${a.height}` : null,
      ),
      aspectRatio: shared((a) => a.aspect_ratio),
      market: shared((a) => a.market),
      language: shared((a) => a.language),
    };
  }, [selectedAssets]);

  // The linked source images whose channel rights are still unknown. While
  // there are any, the server skips every other answer for these assets, so
  // the form locks the field instead. A source in the selection is not one:
  // answering a hero and its crops together is a single consistent edit.
  const selectedIds = new Set(selectedAssets.map((asset) => asset.id));
  const sourceIds = [
    ...new Set(
      selectedAssets
        .filter((asset) => asset.asset_type !== 'source_image')
        .flatMap((asset) => asset.source_image_ids)
        .filter((id) => !selectedIds.has(id)),
    ),
  ];
  const sources = useQueries({
    queries: sourceIds.map((id) => ({
      queryKey: ['asset-library', 'asset', id],
      queryFn: () => getAsset(id),
      enabled: !readOnly,
    })),
  });
  const unknownChannelSources = sources.flatMap(({ data }) =>
    data?.channel_rights?.includes(UNKNOWN_VALUES.channel_rights)
      ? [data.source_image_code ?? data.id]
      : [],
  );

  return (
    <div ref={rootRef} className="flex flex-col gap-3">
      <MetadataFields
        value={form.value}
        onFieldChange={setField}
        mixed={form.mixed}
        readOnly={readOnly}
        assetTypes={assetTypes}
        dimensions={facts.dimensions}
        aspectRatio={facts.aspectRatio}
        market={facts.market}
        language={facts.language}
        unknownChannelSources={unknownChannelSources}
      />

      {!readOnly && (
        <button
          type="button"
          disabled={!canSave}
          title={blockedReason}
          onClick={() => mutation.mutate({ quiet: false })}
          className="mt-1 rounded-lg bg-[#0014cc] px-3 py-1.5 text-[12.5px] font-medium text-white transition-colors hover:bg-[#0010a3] disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
        >
          {/* Just "Save" — no count. The header above already says how many are
              being edited, and repeating it in the label makes the button read
              as arithmetic. */}
          {mutation.isPending ? 'Saving…' : 'Save'}
        </button>
      )}

      {/* Not for carousels: each frame names its own source image, edited in
          the Frames section, and the carousel's links follow from those. */}
      {!onlyCarousels && <SourceImageSection selectedAssets={selectedAssets} readOnly={readOnly} />}
    </div>
  );
}
